import { InjectionToken, isDevMode } from '@angular/core';

import type { SbSDKOptions } from '@storyblok/js';
import { storyblokInit } from '@storyblok/js';
import type { ISbCache, ISbResult } from 'storyblok-js-client';
import StoryblokClient from 'storyblok-js-client';

// Timeout the server fetch after 8 seconds so it will not block the server indefinitely if Storyblok is not responding. The client will retry the request after 1 second, so this should be enough time for the server to recover and respond to the next request.
const SB_SERVER_FETCH_TIMEOUT_MS = 8000;

export function fetchWithCookiesEnabled(
	input: string | URL | globalThis.Request,
	init?: RequestInit,
	cookies?: string,
) {
	if (isDevMode()) {
		if (init) {
			init.credentials = 'include';
		}
	}

	// Apply the timeout only on the server; in the browser the SDK manages its
	// own request lifecycle and we don't want to change client behavior.
	/* v8 ignore start */
	if (globalThis.window === undefined) {
		init = {
			...init,
			signal:
				init?.signal ?? AbortSignal.timeout(SB_SERVER_FETCH_TIMEOUT_MS),
		};

		if (cookies) {
			const headers = new Headers(init.headers);
			headers.set('cookie', cookies);
			init.headers = headers;
		}
	}
	/* v8 ignore stop */

	return fetch(input, init);
}

export function createStoryblokFetch(cookies?: string) {
	return (input: string | URL | globalThis.Request, init?: RequestInit) =>
		fetchWithCookiesEnabled(input, init, cookies);
}

export const SB_CLIENT_FACTORY = new InjectionToken<
	typeof initializeStoryblokClient
>('storyblok-client-factory');

// The SDK keeps its `memory` cache at module level, so on the server it is
// shared by every render in the process.
const SHARED_MEMORY_CACHE: ISbCache = { clear: 'auto', type: 'memory' };

/**
 * A cache only one client sees. Needed when the same story URL must answer
 * differently per request, as the apimock scenario of an e2e session does.
 */
export function createRequestScopedCache(): ISbCache {
	let entries: Record<string, ISbResult> = {};

	return {
		clear: 'auto',
		type: 'custom',
		custom: {
			get: (key) => Promise.resolve(entries[key]),
			set: (key, content) => {
				entries = { ...entries, [key]: content };
				return Promise.resolve();
			},
			getAll: () => Promise.resolve(),
			flush: () => {
				entries = {};
				return Promise.resolve();
			},
		},
	};
}

export function initializeStoryblokClient(
	accessToken: string,
	endpoint: string | null,
	cookies?: string,
	cache: ISbCache = SHARED_MEMORY_CACHE,
	isServer: boolean = globalThis.window === undefined,
): StoryblokClient | undefined {
	return storyblokInit({
		accessToken,
		use: [
			// This is a modified ApiFactory, just to be able to alter the endpoint parameter in the
			// StoryblokClient
			(options: SbSDKOptions) => {
				const { apiOptions } = options;
				/* v8 ignore start */
				if (!apiOptions?.accessToken) {
					console.error(
						'You need to provide an access token to interact with Storyblok API. Read https://www.storyblok.com/docs/api/content-delivery#topics/authentication',
					);
					return {};
				}
				/* v8 ignore stop */
				const storyblokApi = new StoryblokClient(
					{
						...apiOptions,
						cache,
						// The SDK's tiered throttle queues protect long-lived
						// browser apps from rate limits (down to 6 req/s for
						// per_page=100 queries). On the server every render
						// gets its own short-lived client (its cache is still
						// shared, see SHARED_MEMORY_CACHE), so queueing only
						// adds latency — lift the limit to the SDK maximum.
						...(isServer && {
							rateLimit: 1000,
						}),
						fetch: createStoryblokFetch(cookies),
					},
					endpoint ?? '',
				);
				return { storyblokApi };
			},
		],
	}).storyblokApi;
}
